"use client"

import React, {useState, useSyncExternalStore} from "react";

import {Dialog, DialogContent, DialogTitle} from "@mui/material";
import {useTranslations} from "use-intl";
import styles from "./AddToHome.module.scss";

// Le funzioni di detection sono pure rispetto al runtime: leggono
// `navigator.userAgent`, quindi sono safe da chiamare in render solo
// quando siamo sul client. Le centralizziamo qui per usarle via
// `useSyncExternalStore` (pattern raccomandato da React 18+ per stato
// esterno: niente cascading renders post-mount, ne' setState in
// effect).

type Device = {ios: boolean; android: boolean; desktop: boolean};

const DEFAULT_DEVICE: Device = {ios: false, android: false, desktop: false};

// useSyncExternalStore richiede che getSnapshot ritorni la STESSA
// reference per lo stesso stato esterno. Il device non cambia durante
// la vita della pagina: cacchiamolo a module-level alla prima
// chiamata client-side. Senza questa cache, ogni render creerebbe un
// nuovo oggetto, React lo interpreterebbe come cambio di snapshot e
// genererebbe un loop "Maximum update depth exceeded".
let cachedDevice: Device | null = null;

const detectDevice = (): Device => {
  if (cachedDevice) return cachedDevice;
  if (typeof navigator === "undefined") return DEFAULT_DEVICE;
  const ua = navigator.userAgent.toLowerCase();
  cachedDevice = {
    ios:
      /iphone|ipad|ipod/.test(ua) && /safari/.test(ua) && !/chrome/.test(ua),
    android: /android/.test(ua) && !/safari/.test(ua),
    desktop: !/android|iphone|ipad|ipod/.test(ua),
  };
  return cachedDevice;
};

const subscribeNoop = () => () => {};
const getServerDevice = (): Device => DEFAULT_DEVICE;

// matchMedia('(display-mode: standalone)') puo' cambiare se l'app
// viene "installata" mid-session, quindi ci abboniamo davvero.
const subscribeStandalone = (cb: () => void) => {
  if (typeof window === "undefined") return () => {};
  const mql = window.matchMedia("(display-mode: standalone)");
  mql.addEventListener("change", cb);
  return () => mql.removeEventListener("change", cb);
};
const getStandalone = (): boolean =>
  typeof window !== "undefined" &&
  window.matchMedia("(display-mode: standalone)").matches;
// SSR: assumiamo standalone per non far comparire il prompt al primo
// render server-side e ottenere un'hydration coerente con il caso
// peggiore (no prompt). Il client riconcilia subito al valore reale.
const getStandaloneServer = () => true;

export default function AddToHome() {
  const t = useTranslations("AddToHome");
  const [showPrompt, setShowPrompt] = useState(false);

  const device = useSyncExternalStore(
    subscribeNoop,
    detectDevice,
    getServerDevice,
  );
  const isStandalone = useSyncExternalStore(
    subscribeStandalone,
    getStandalone,
    getStandaloneServer,
  );

  const closeDialog = () => {
    setShowPrompt(false);
  };

  if (isStandalone) return null;

  return (
    <>
      <div
        className={styles.alert}
        onClick={() => {
          setShowPrompt(true);
        }}
      >
        <div>{t("title")}</div>
        <small>{t("cta")}</small>
      </div>
      <Dialog
        open={showPrompt}
        keepMounted
        onClose={closeDialog}
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle>
          <div>{t("title")}</div>
        </DialogTitle>
        <DialogContent>
          <p className={styles.info}>
            {device.ios && <>{t("text_ios")}</>}
            {device.android && <>{t("text_android")}</>}
            {(device.desktop ||
              (!device.ios && !device.android && !device.desktop)) && (
              <>{t("text")}</>
            )}
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
}
